import groq from 'groq';
import { ComponentType, EntityType } from '../types';

export const ImageComponentFragment = groq`
  ...select(
    defined(image) => {
      "image": image {
        _type, 
        asset->{
          _id,
          _type,
          altText,
          description,
          metadata {
            dimensions {
              ...
            }
          }
        }
      },
      'imageRef': image
    },
    {"image": image}
  )
`;

export const GetImageComponentQuery = groq`
*[_type == "${ComponentType.Image}" && _id == $id]{
  ...,
  ${ImageComponentFragment}
}[0]`;

// Modal layout's grid items should include allowed types
// specified in `sanity-block-web/studio/schemas/components/modal.ts`.
// Resolve references at all nesting levels for any components that contain them.
export const ModalComponentFragment = groq`
  defined(modal) => {
    "modal": modal->{
      ...,
      layout {
        ...,
        gridItems[]{
          ...,
          component->{
            ...,
            ${ImageComponentFragment}
          }
        }
      }
    }
  }
`;

export const HierarchyItemsFragment = groq`
  "hierarchyItems": *[_type == "${EntityType.Campaign}" && references(^._id)] {
      hierarchy{ hierarchyItems[] {
        _key,
        id,
        parent,
        page->{_id, _type, name, slug},
      }}
    }[0].hierarchy.hierarchyItems
`;

export const HierarchyItemsFragmentWithPages = groq`
  "hierarchyItems": *[_type == "${EntityType.PageTree}" && references(^._id)] {
      hierarchy{ hierarchyItems[] {
        _key,
        id,
        parent,
        page->{_id, _type, name, slug},
      }}
    }[0].hierarchy.hierarchyItems
`;

export const RichTextFragment = groq`
    ...,
    markDefs[] {
      ...,
      defined(page) => {
        page->{
          _id,
          _type,
          name,
          slug,
          _type == "${EntityType.CampaignPage}" => {
            ${HierarchyItemsFragment},
          },
          _type == "${EntityType.Page}" => {
            ${HierarchyItemsFragmentWithPages},
          },
          _type == "${EntityType.NewsTopicPage}" => {
            topic->{slug}
          },
        },
      },
      ${ModalComponentFragment},
    }
`;

export const TextComponentFragment = groq`
  body[] {
    ${RichTextFragment}
  },
  'bodyRef': body
`;

export const VideoAnimationFragment = groq`
  videoAnimation {
    ...,
    "webm": webm.asset->url,
    "mov": mov.asset->url
  }
`;

export const GetLoadingScreenQuery = groq`*[_type == "${EntityType.LoadingScreen}"][0]`;

export const GetFooterQuery = groq`*[_type == "${EntityType.Footer}"]{
  ...,
  "companyLinks" : companyLinks[] {
    _type == 'reference' => @->{
      "_id": @._id,
      "_type": @._type,
      "url": link.url,
      "text": link.text
    },
  },
  "buLinks" : buLinks[] {
    _type == 'reference' => @->{
      "_id": @._id,
      "_type": @._type,
      "url": link.url,
      "text": link.text
    },
  },
  "socialLinks" : socialLinks[] {
    _type == 'reference' => @->{
      "_id": @._id,
      "_type": @._type,
      "url": link.url,
      "text": link.text
    },
  },
}[0]`;

export const VideoComponentFragment = groq`
  defined(muxVideo) => {
    "muxVideo": muxVideo.asset->
  }
`;

export const GetVideoQuery = groq`
  *[_type == "${ComponentType.Video}" && _id == $id]{
    ...,
    ${VideoComponentFragment}
  }[0]
`;

export const MediaComponentFragment = groq`
...,
${ImageComponentFragment},
defined(video) => {
  "video": video -> {
    ...,
    ${VideoComponentFragment}
  }
},
caption {
  ...,
  copy[] {
    ${RichTextFragment}
  }
}
`;

export const GetMediaComponent = groq`
*[_type == "${ComponentType.Media}" && _id == $id]{
  ${MediaComponentFragment}
}[0]
`;

export const GetTextQuery = groq`
  *[_type == "${ComponentType.Text}" && _id == $id]{
    ...,
    ${TextComponentFragment}
  }[0]
`;

export const IconStackComponentPropertiesFragment = groq`
  ...,
  ${TextComponentFragment},
  icon->
`;

export const GetIconStackQuery = groq`
  *[_type == "${ComponentType.IconStack}" && _id == $id]{
    ${IconStackComponentPropertiesFragment}
  }[0]
`;

// Accordion Item's grid items should include allowed types
// specified in `sanity-block-web/studio/schemas/components/accordionItem.ts`.
// Resolve references at all nesting levels for any components that contain them.
export const AccordionItemComponentFragment = groq`
  ...,
  contents {
    ...,
    gridItems[]{
      ...,
      component->{
        ...,
        ${ImageComponentFragment},
        ${TextComponentFragment}
      }
    }
  }
`;

export const GetAccordionItemQuery = groq`
*[_type == "${ComponentType.AccordionItem}" && _id == $id]{
  ${AccordionItemComponentFragment}
}[0]
`;

export const GetAccordionQuery = groq`
*[_type == "${ComponentType.Accordion}" && _id == $id]{
  ...,
  items[]-> {
    ${AccordionItemComponentFragment}
  }
}[0]
`;

export const QuoteComponentFragment = groq`
...,
copy[] {
  ${RichTextFragment}
},
authorInfo {
  ...,
  name[] {
    ${RichTextFragment}
  },
  title[] {
    ${RichTextFragment}
  }
}
`;

export const GetQuoteComponent = groq`
*[_type == "${ComponentType.Quote}" && _id == $id]{
  ${QuoteComponentFragment}
}[0]
`;

export const VanityNumberComponentFragement = groq`
...,
description[] {
  ${RichTextFragment}
}
`;

export const GetVanityNumberComponent = groq`
*[_type == "${ComponentType.VanityNumber}" && _id == $id]{
  ${VanityNumberComponentFragement}
}[0]
`;

export const GetLinkQuery = groq`*[_id == $id && _type == "${ComponentType.Link}"][0]`;

export const GetButtonQuery = groq`*[_id == $id && _type == "${ComponentType.Button}"][0]`;

export const GetNewsRunningLaneQuery = groq`*[_id == $id && _type == "${ComponentType.NewsRunningLane}"][0]`;

export const GetDocumentQuery = groq`*[_id == $id][0]`;

export const CarouselFragment = groq`
  images[] {..., asset->{
    _id,
    _type,
    altText,
    description,
    metadata {
      dimensions {
        ...
      }
    }
  }},
  "imageRefs": images
`;

export const GetCarouselComponentQuery = groq`
*[_type == "${ComponentType.Carousel}" && _id == $id]{
  ...,
  ${CarouselFragment}
}[0]`;
